import { useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  User,
  Calendar,
  Settings,
  SquarePen,
  Power,
  Trash2,
  Shield,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

import { useTranslation } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import useModal from '../../hooks/useModal';
import {
  useAdminDetail,
  useAdminAuditLogs,
  useAdminLoginLogs,
  useUpdateAdmin,
} from '../../hooks/queries/useAdminQueries';
import {
  COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE,
} from '../../constants/countryCodes';
import { getImageUrl } from '../../utils/imageUrl';
import { getActionStyle } from '../../utils/auditStyles';
import { formatDate, formatDateTime } from '../../utils/formatDate';
import { IBackofficeAdmin, IAuditLog } from '../../types';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../ui/table';
import AuditExpandedDetail from '../AuditExpandedDetail';
import AdminFormDialog from './AdminFormDialog';
import AdminDeleteDialog from './AdminDeleteDialog';

const PERMISSION_KEYS = [
  { key: 'users', labelKey: 'permissions.users' },
  { key: 'spaces', labelKey: 'permissions.spaces' },
  { key: 'feedback', labelKey: 'permissions.feedback' },
  { key: 'reports', labelKey: 'permissions.reports' },
  { key: 'x_posts', labelKey: 'permissions.x_posts' },
  { key: 'audit_logs', labelKey: 'permissions.audit_logs' },
  { key: 'settings', labelKey: 'permissions.settings' },
  { key: 'admins', labelKey: 'permissions.admins' },
];

const AdminDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [detailTab, setDetailTab] = useState<'activity' | 'login'>('activity');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: admin, isLoading } = useAdminDetail(id || '');
  const { data: auditLogs, isLoading: auditLoading } = useAdminAuditLogs(
    id || '',
  );
  const { data: loginLogs, isLoading: loginLogsLoading } = useAdminLoginLogs(
    id || '',
  );

  const updateAdmin = useUpdateAdmin();

  const editFormDialog = useModal<IBackofficeAdmin>();
  const deleteDialog = useModal<IBackofficeAdmin>();

  if (isLoading || !admin) {
    return (
      <div className="p-4 lg:p-8 text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  const isOwner = currentUser?.isOwner ?? false;
  const isSelf = currentUser?.id === admin.id;
  const canManage = isOwner && !isSelf;

  const handleBack = () => navigate('/admin-management');

  const handleToggleActive = () => {
    updateAdmin.mutate({
      id: admin.id,
      data: { isActive: !admin.isActive },
    });
  };

  const countryLabel = COUNTRY_CODES.find(
    (c) => c.code === (admin.countryCode || DEFAULT_COUNTRY_CODE),
  )?.label;

  return (
    <div className="p-8">
      {/* Back button */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> {t('admin.back_to_list')}
        </Button>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 rounded-full bg-linear-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-3xl overflow-hidden shrink-0">
              {admin.avatar ? (
                <img
                  src={getImageUrl(admin.avatar)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                admin.displayName.charAt(0).toUpperCase()
              )}
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-foreground">
                  {admin.displayName}
                </h1>
                <Badge variant={admin.isActive ? 'success' : 'muted'}>
                  {admin.isActive ? t('common.active') : t('admin.disabled')}
                </Badge>
              </div>
              <div className="flex items-center gap-2 mt-2 text-muted-foreground">
                <User className="w-4 h-4" />
                <span>@{admin.username}</span>
              </div>
              {admin.email && (
                <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  <span>{admin.email}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          {canManage && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={() => editFormDialog.open(admin)}
                className="flex items-center gap-2"
              >
                <SquarePen className="w-4 h-4" />
                {t('admin.edit_profile')}
              </Button>

              <Button
                variant="outline"
                onClick={handleToggleActive}
                className={`flex items-center gap-2 ${admin.isActive ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`}
              >
                <Power className="w-4 h-4" />
                {admin.isActive
                  ? t('admin.disable_account')
                  : t('admin.enable_account')}
              </Button>
              <Button
                variant="outline"
                onClick={() => deleteDialog.open(admin)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {t('admin.delete_account')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Account Info */}
        <Card className="lg:col-span-1">
          <CardContent className="p-6">
            <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              {t('admin.account_info')}
            </h3>
            <div className="space-y-0">
              <div className="flex items-start gap-3 pb-3 border-b border-border">
                <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-muted-foreground">
                    {t('common.email')}
                  </div>
                  <div className="text-sm text-foreground mt-1">
                    {admin.email || '—'}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 py-3 border-b border-border">
                <User className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-muted-foreground">
                    {t('account.mobile')}
                  </div>
                  <div className="text-sm text-foreground mt-1">
                    {admin.mobileNumber
                      ? `${countryLabel || `+${admin.countryCode || DEFAULT_COUNTRY_CODE}`} ${admin.mobileNumber}`
                      : '—'}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 py-3 border-b border-border">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-muted-foreground">
                    {t('common.created_at')}
                  </div>
                  <div className="text-sm text-foreground mt-1">
                    {formatDate(admin.createdAt)}
                  </div>
                </div>
              </div>
              {/* Permissions */}
              <div className="flex items-start gap-3 pt-3">
                <Settings className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-muted-foreground mb-2">
                    {t('permissions.title')}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(admin.permissions ?? []).length > 0 ? (
                      (admin.permissions ?? []).map((perm) => {
                        const permDef = PERMISSION_KEYS.find(
                          (p) => p.key === perm,
                        );

                        return (
                          <Badge key={perm} variant="outline">
                            {permDef ? t(permDef.labelKey) : perm}
                          </Badge>
                        );
                      })
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right: Tabs */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <div className="inline-flex items-center rounded-lg bg-muted p-1 w-full mb-6">
              <button
                onClick={() => setDetailTab('activity')}
                className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-all ${
                  detailTab === 'activity'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('admin.activity_log')}
              </button>
              <button
                onClick={() => setDetailTab('login')}
                className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-all ${
                  detailTab === 'login'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('admin.login_history')}
              </button>
            </div>

            {detailTab === 'activity' && (
              <div>
                {auditLoading ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    {t('common.loading')}
                  </div>
                ) : !auditLogs || auditLogs.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    {t('admin.no_activity')}
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-border">
                    <Table className="table-fixed">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[22%]">
                            {t('audit.admin_id')}
                          </TableHead>
                          <TableHead className="w-[18%]">
                            {t('audit.module')}
                          </TableHead>
                          <TableHead className="w-[32%]">
                            {t('audit.details')}
                          </TableHead>
                          <TableHead className="w-[28%]">
                            {t('audit.timestamp')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {auditLogs.map((log: IAuditLog) => {
                          const hasDetails =
                            log.beforeValues !== null ||
                            log.afterValues !== null ||
                            log.metadata !== null;
                          const isExpanded = expandedId === log.id;
                          const moduleKey = log.module || 'unknown';
                          const desc =
                            t(`audit.action.${log.action}` as any) ||
                            log.action;

                          return (
                            <Fragment key={log.id}>
                              <TableRow
                                className="hover:bg-muted/50 transition-colors cursor-pointer"
                                onClick={() =>
                                  hasDetails &&
                                  setExpandedId(isExpanded ? null : log.id)
                                }
                              >
                                <TableCell className="text-sm">
                                  <div className="flex items-center gap-2">
                                    {hasDetails ? (
                                      isExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                                      )
                                    ) : (
                                      <span className="w-4 h-4 shrink-0" />
                                    )}
                                    <span className="truncate">
                                      {log.username || '—'}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm font-bold">
                                  {t(`audit.module.${moduleKey}` as any)}
                                </TableCell>
                                <TableCell>
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionStyle(log.action)}`}
                                  >
                                    {desc}
                                  </span>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                  {formatDateTime(log.createdAt)}
                                </TableCell>
                              </TableRow>

                              {isExpanded && hasDetails && (
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="px-4 py-3 bg-muted/30 border-b border-border"
                                  >
                                    <AuditExpandedDetail
                                      beforeValues={log.beforeValues}
                                      afterValues={log.afterValues}
                                      metadata={log.metadata}
                                    />
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            {detailTab === 'login' && (
              <div>
                {loginLogsLoading ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    {t('common.loading')}
                  </div>
                ) : !loginLogs || loginLogs.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    {t('admin.no_login_history')}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {loginLogs.map((log) => {
                      const ua = log.userAgent || '';
                      let browser = 'Unknown Browser';
                      let os = '';

                      if (ua.includes('Chrome') && !ua.includes('Edg'))
                        browser = 'Chrome';
                      else if (ua.includes('Firefox')) browser = 'Firefox';
                      else if (ua.includes('Safari') && !ua.includes('Chrome'))
                        browser = 'Safari';
                      else if (ua.includes('Edg')) browser = 'Edge';

                      if (ua.includes('Windows')) os = 'Windows';
                      else if (ua.includes('Mac OS')) os = 'macOS';
                      else if (ua.includes('Linux')) os = 'Linux';
                      else if (ua.includes('Android')) os = 'Android';
                      else if (ua.includes('iPhone') || ua.includes('iPad'))
                        os = 'iOS';

                      const label = os ? `${browser} on ${os}` : browser;

                      return (
                        <div
                          key={log.id}
                          className="flex items-center justify-between p-3 rounded-lg border border-border"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                                log.success ? 'bg-green-100' : 'bg-red-100'
                              }`}
                            >
                              {log.success ? (
                                <Shield className="w-5 h-5 text-green-600" />
                              ) : (
                                <Shield className="w-5 h-5 text-red-600" />
                              )}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-foreground">
                                {label}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {log.ipAddress || '—'}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge variant={log.success ? 'success' : 'danger'}>
                              {log.success
                                ? t('admin.login_success')
                                : t('admin.login_failed')}
                            </Badge>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {formatDateTime(log.createdAt)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Profile Dialog */}
      <AdminFormDialog
        isOpen={editFormDialog.isOpen}
        onClose={editFormDialog.close}
        admin={editFormDialog.selectedItem}
      />

      {/* Delete Admin Dialog */}
      <AdminDeleteDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => {
          deleteDialog.close();
          navigate('/admin-management');
        }}
        admin={deleteDialog.selectedItem}
      />
    </div>
  );
};

export default AdminDetailView;
